import { Link } from 'react-router-dom'
import { Facebook, Instagram, Youtube, MessageCircle } from 'lucide-react'
import evtimiaAlb from '@/assets/evtimia-alb.png'
import { useTranslation } from '@/i18n'

const TikTokIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.7a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.13z" />
  </svg>
)

const socialLinks = [
  { icon: Facebook, href: 'https://www.facebook.com/evtimiaagency', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/_evtimia', label: 'Instagram' },
  { icon: TikTokIcon, href: 'https://www.tiktok.com/@evtimia_a', label: 'TikTok', isCustom: true },
  { icon: Youtube, href: 'https://youtube.com/@evtimia', label: 'YouTube' },
  { icon: MessageCircle, href: 'https://wa.me/+40742055050', label: 'WhatsApp' },
]

export function Footer() {
  const { t } = useTranslation()

  return (
    <footer className="py-10 sm:py-14" style={{ backgroundColor: '#1A1A2E' }}>
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
          <div>
            <Link to="/">
              <img src={evtimiaAlb} alt="EVTIMIA" width={104} height={32} loading="lazy" decoding="async" className="h-8 mb-4" />
            </Link>
            <p className="text-white/60 leading-relaxed text-sm">
              {t.footer.description}
            </p>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-base">{t.footer.companyDetails}</h3>
            <div className="text-white/60 text-sm space-y-1">
              <p>str. Albert Einstein, nr. 23/1</p>
              <p>Cluj-Napoca, jud. Cluj</p>
              <p className="mt-2">CUI: 52191515</p>
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-base">{t.footer.quickLinks}</h3>
            <nav className="flex flex-col gap-2">
              <Link to="/services" className="text-white/60 hover:text-white gentle-animation text-sm">{t.nav.services}</Link>
              {/* <Link to="/portfolio" className="text-white/60 hover:text-white gentle-animation text-sm">{t.nav.work}</Link> */}
              <Link to="/about" className="text-white/60 hover:text-white gentle-animation text-sm">{t.nav.about}</Link>
              <Link to="/contact" className="text-white/60 hover:text-white gentle-animation text-sm">{t.nav.contact}</Link>
            </nav>
          </div>
        </div>

        <div className="flex justify-end gap-4 mb-6">
          {socialLinks.map(({ icon: Icon, href, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className="-m-3 p-3 min-w-11 min-h-11 inline-flex items-center justify-center text-white/50 hover:text-white gentle-animation"
            >
              <Icon size={18} />
            </a>
          ))}
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} EVTIMIA. {t.footer.allRights}
          </p>
          {/* Each link carries its "|" in front of it, in a 2rem box that the
              list's -ml-8 pushes out past the nav's clipped left edge for
              whichever link starts a line. So when the links wrap on phones,
              no line starts with a "|". The links stay inline, so their padding
              enlarges the tap area without spreading the wrapped lines apart. */}
          <nav className="overflow-hidden pl-1 text-sm pr-20 sm:pr-0">
            <ul className="-ml-8 flex flex-wrap items-center gap-y-2">
              {[
                { to: '/privacy-policy', label: t.footer.privacyPolicy },
                { to: '/terms', label: t.footer.terms },
                { to: '/cookie-policy', label: t.footer.cookiePolicy },
                { href: 'https://reclamatiisal.anpc.ro/', label: t.footer.sal },
              ].map(item => (
                <li
                  key={item.label}
                  className="before:inline-block before:w-8 before:text-center before:text-white/30 before:content-['|']"
                >
                  {item.to ? (
                    <Link to={item.to} className="py-1 text-white/50 hover:text-white gentle-animation">{item.label}</Link>
                  ) : (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-1 text-white/50 hover:text-white gentle-animation"
                    >
                      {item.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  )
}
